<template>
    <div>
        <b-row class="mb-4">
            <b-col cols="8">
                <h5>Kan niet leveren</h5>
            </b-col>
            <b-col v-if="!isPartialUnableToShipAllowed" cols="4" class="text-right">
                <small class="text-info" v-b-tooltip.click="`Als deel-manco's niet zijn toegestaan wordt altijd de gehele levering op manco gezet, ook bij één manco product. De overige producten uit zo'n levering komen dan dus ook niet meer naar voren als scan suggestie.`">
                    <font-awesome-icon icon="question-circle"/>
                </small>
            </b-col>
        </b-row>

        <div v-if="isLoadingSuggestedLines">
            <font-awesome-icon icon="spinner" spin/>
            <b-button variant="link" @click="$emit('close')" size="sm" class="d-block mt-4 p-0">
                <small>&lt; terug</small>
            </b-button>
        </div>
        <div v-else-if="!selectedProduct" class="mt-2">
            <form @submit.prevent="saveUnableToShip">
                <div>
                    <b-table-simple small borderless class="mb-0">
                        <b-thead>
                        <b-tr>
                            <b-th>Productnr</b-th>
                            <b-th>Productnaam</b-th>
                            <b-th>Totaal aantal</b-th>
                            <b-th>Niet leverbaar</b-th>
                        </b-tr>
                        </b-thead>
                        <b-tbody>
                        <b-tr v-for="product in products">
                            <b-td class="align-middle">
                                <product-number :number="product.identifier" :product-name="product.name" link-class="text-secondary"
                                                :show-only-fields="['name', 'identifier', 'image', 'barcodes']"
                                />
                            </b-td>
                            <b-td class="align-middle">
                                <span v-if="product.name">{{ product.name }}</span>
                                <i v-else>onbekend</i>
                            </b-td>
                            <b-td class="align-middle">{{ product.quantity }}</b-td>
                            <b-td class="align-middle">
                                <b-input v-model.number="product.quantitySelected" type="number" size="sm"
                                         min="0" :max="product.quantity"
                                         style="width:50px;"
                                         placeholder="0"
                                />
                            </b-td>
                        </b-tr>
                        <b-tr v-if="!products.length">
                            <b-td colspan="4">
                                <i>Geen scanbare producten</i>
                            </b-td>
                        </b-tr>
                        </b-tbody>
                    </b-table-simple>
                </div>

                <b-table-simple small borderless class="mb-0 mt-3">
                    <b-tr v-if="products.length">
                        <b-td colspan="4" class="pb-4">
                            <h6>Selecteer reden</h6>
                            <div v-for="(reason, index) in unableToShipReasons">
                                <input v-model="selectedReason" type="radio" name="unableToShipReasons" :id="`reason-${index}`" class="mr-2 align-middle" :value="reason" required/>
                                <label :for="`reason-${index}`" class="mb-1">{{ reason }}</label>
                            </div>
                        </b-td>
                    </b-tr>
                    <b-tr>
                        <b-td>
                            <b-button variant="link" @click="$emit('close')" size="sm" class="p-0">
                                <small>&lt; terug</small>
                            </b-button>
                        </b-td>
                        <b-td v-if="products.length" colspan="3" class="text-right">
                            <b-button variant="danger" type="submit" size="sm" :disabled="!hasSelectedProducts || isSaving">
                                Kan niet leveren
                                <font-awesome-icon v-if="isSaving" icon="spinner" spin class="ml-1"/>
                            </b-button>
                        </b-td>
                    </b-tr>
                </b-table-simple>

                <b-alert :show="!!errorMessage" variant="danger" class="mt-4 mb-0">
                    <small>{{ errorMessage }}</small>
                </b-alert>
            </form>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import _ from "lodash";
import ProductInfo from "@/components/ProductInfo.vue";
import ProductNumber from "@/components/ProductNumber.vue";
import {getLocationPropertyValue} from "@/helpers/locations";

export default {
    name: 'UnableToShip',
    components: {ProductNumber, ProductInfo},

    props: {
        suggestedLines: {required: true, type: Array},
        storageZoneIdentifier: {required: true, type: String},
        location: {required: true, type: Object},
        batchModelType: {required: true, type: String},
        batchModelIdentifier: {required: true, type: String},
        processType: {required: true, type: String},
    },

    data() {
        return {
            isLoadingSuggestedLines: true,
            isSaving: false,
            products: [],
            productNamesByIdentifier: {},
            unableToShipReasons: [],
            selectedProduct: null,
            selectedReason: null,
            errorMessage: null,
            suggestedLinesInternal: [],
        }
    },

    async mounted() {
        if (!this.suggestedLines?.length) {
            await this.loadSuggestedLines()
        }
        
        this.isLoadingSuggestedLines = false
        this.initializeProducts()

        await this.$store.getters.getConfig
            .then(() => {
                _.each(this.$store.state.config.settings['frontend-parameters.process-products-sort']['unable-to-ship-reasons'], (reason) => {
                    this.unableToShipReasons.push(reason.description)
                })
            })
    },

    computed: {
        hasSelectedProducts() {
            return this.products.some(product => product.quantitySelected)
        },
        loadedSuggestedLines() {
            return (this.suggestedLines?.length)
                ? this.suggestedLines
                : this.suggestedLinesInternal
        },
        isPartialUnableToShipAllowed() {
            return getLocationPropertyValue(this.location.location_properties, 'processProducts.allowPartialUnableToShip')
        },
    },

    methods: {
        getLocationPropertyValue,
        async loadSuggestedLines() {
            return this.$http.get(`/api/rest/locations/${this.location.location_type.identifier}/${this.location.identifier}/storage-locations/${this.storageZoneIdentifier}/process-product/${this.processType}/suggest-scan-product`, {
                params: {
                    dispatch_request_batch_identifier: this.batchModelIdentifier.toString(),
                },
            })
                .then(response => {
                    this.suggestedLinesInternal = response.data
                })
        },
        initializeProducts() {
            this.productNamesByIdentifier = _.chain(this.loadedSuggestedLines)
                .map(order => order.sales_order_line)
                .mapKeys(line => line.product_identifier)
                .mapValues(line => line.product_name)
                .value()

            this.products = _.chain(this.loadedSuggestedLines)
                // Groepeer op unieke producten en tel de quantities op
                .groupBy('product_identifier')
                .map(lines => ({
                    identifier: lines[0].product_identifier,
                    name: this.productNamesByIdentifier[lines[0].product_identifier] ?? null,
                    quantity: _.sumBy(lines, 'quantity_pending'),
                    quantitySelected: '',
                }))
                .flatten(1)
                .sortBy(product => product.name.toLowerCase())
                .value()

        },
        saveUnableToShip() {
            this.isSaving = true
            this.errorMessage = null

            axios.post(`/api/rest/locations/${this.location.location_type.identifier}/${this.location.identifier}/dispatch-request-batches/${this.batchModelIdentifier}/unable-to-ship`, {
                reason: this.selectedReason,
                products: this.products
                    .filter(p => p.quantitySelected)
                    .map(p => ({
                        identifier: p.identifier,
                        quantity: p.quantitySelected,
                    })),
            })
                .then(response => {
                    const totalQuantity = _.sumBy(this.products, p => parseInt(p.quantitySelected ? p.quantitySelected : 0))
                    this.$emit('close', `${totalQuantity} niet leverbare producten verwerkt`)
                })
                .catch(error => {
                    this.errorMessage = `Fout bij het verwerken. Neem contact op met de beheerder.`
                })
                .finally(() => this.isSaving = false)
        },
    },
}
</script>
